/**
 * Accommodations switching composable
 * Handles switching between accommodations and organisations
 */

import { store } from '@/store'
import { computed, type ComputedRef } from 'vue'
import { selectedAccommodationId, setSelectedAccommodationId } from '@/layouts/queries'

import type { Accommodation, Organisation } from '@/types/models'

/** Return type of useAccommodations composable */
export interface UseAccommodationsReturn {
  /** Switch to different accommodation */
  switchAccommodation: (
    newSelectedAccommodation: Accommodation | null | undefined,
    organisation: Organisation | null | undefined
  ) => Promise<void>
  /** Switch to different organisation */
  switchOrganisation: (newOrganisation: Organisation | null | undefined) => Promise<void>
}

/**
 * Accommodation and organisation switching utilities
 * Manages context switching between properties and organisations
 * @returns Switch functions
 */
export default function useAccommodations(): UseAccommodationsReturn {
  const selectedOrganisationId: ComputedRef<string | undefined> = computed(
    () => store.state.session.selectedOrganisation?.id
  )

  /**
   * Switch to different accommodation
   * Handles cross-organisation switching if needed
   * @param newSelectedAccommodation - Accommodation to switch to
   * @param organisation - Organisation the accommodation belongs to
   */
  async function switchAccommodation(
    newSelectedAccommodation: Accommodation | null | undefined,
    organisation: Organisation | null | undefined
  ): Promise<void> {
    const newSelectedAccommodationId = newSelectedAccommodation?.accommodationId

    if (!newSelectedAccommodationId || newSelectedAccommodationId === selectedAccommodationId.value) return

    // If accommodation belongs to different organisation, switch org first
    if (organisation?.id !== selectedOrganisationId.value) {
      await store.dispatch('session/switchOrganisation', {
        newOrganisation: organisation,
        targetAccommodationId: newSelectedAccommodationId,
      })
      return
    }

    // Same organisation, just switch accommodation
    setSelectedAccommodationId(newSelectedAccommodationId)
  }

  /**
   * Switch to different organisation
   * @param newOrganisation - Organisation to switch to
   */
  async function switchOrganisation(newOrganisation: Organisation | null | undefined): Promise<void> {
    if (!newOrganisation || newOrganisation?.id === selectedOrganisationId.value) return

    await store.dispatch('session/switchOrganisation', { newOrganisation })
  }

  return {
    switchAccommodation,
    switchOrganisation,
  }
}